import React, { useCallback, useContext, useMemo, useState, type Ref } from 'react';
import { ENTITY_LIMIT_TYPE } from '@jira/polaris__domain-collection/src/entityLimitType.constants.tsx';
import { useNavigationCollectionsActions } from '@jira/polaris__component-navigation-collections-store/src/useNavigationCollectionsActions.tsx';
import { useEntityLimitMessage } from '@jira/polaris__component-limits/src/useEntityLimitMessage.tsx';
import {
	useCollectionsDataCount,
	useCollectionsIsLoading,
	useCollectionsInitialized,
	useCollectionsError,
} from '@jira/polaris__component-navigation-collections-store/src/useCollectionsIsLoading.tsx';
import { useEntityLimitsByType } from '@jira/polaris__component-entity-limits-store/src/useEntityLimitsByType.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useIsReadOnly } from './common/utils/use-is-read-only.tsx';
import { AnalyticContext } from './common/controllers/analytic-context/index.tsx';
import { useNotifications } from './common/utils/notifications/index.tsx';
import { CommonNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/common-nav-menu-button/main.tsx';
import Tooltip from '@atlaskit/tooltip';
import AddIcon from '@atlaskit/icon/core/add';
import { Trigger, usePanel } from '@atlassian/entry-point-panel';
import { createRoadmapPanelAsync } from './createRoadmapPanel.entrypoint.tsx';

export const CREATE_ROADMAP = 'createRoadmaps';

const createRoadmap = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.common.action-buttons.create-roadmap',
	defaultMessage: 'Create roadmap',
	description: 'Used as the alt text for an icon button that when used navigates to create roadmap',
});

const errorTitle = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.common.action-buttons.error-title',
	defaultMessage: 'We couldn’t create roadmap',
	description: 'Flag title to communicate an error while loading Roadmaps in Navigation.',
});

const errorDescription = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.common.action-buttons.error-description',
	defaultMessage: 'Please try again or refresh the page.',
	description: 'Flag description for an error occurring while loading Roadmaps in Navigation.',
});

const errorRefresh = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.common.action-buttons.error-refresh',
	defaultMessage: 'Try again',
	description: 'Action visible in the Flag to refresh Roadmaps in Navigation.',
});

const infoTitleLimit = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-roadmaps.common.action-buttons.info-title-limit',
	defaultMessage: 'Reached limit',
	description: 'Flag title to communicate information about reaching a limit for a site.',
});

const useHasReachedCollectionsLimitMessage = () => {
	const entityLimitMessage = useEntityLimitMessage();
	const collectionsLimit = useEntityLimitsByType(ENTITY_LIMIT_TYPE.COLLECTIONS_PER_SITE);

	return useCallback(
		(collectionsCount: number) => {
			if (collectionsLimit && collectionsCount >= collectionsLimit) {
				return entityLimitMessage(ENTITY_LIMIT_TYPE.COLLECTIONS_PER_SITE, collectionsLimit);
			}

			return null;
		},
		[collectionsLimit, entityLimitMessage],
	);
};

export const CreateRoadmapButton: () => React.JSX.Element = () => {
	const { formatMessage } = useIntl();
	const [isLoading, setIsLoading] = useState(false);
	const isReadOnly = useIsReadOnly();
	const { loadCollections } = useNavigationCollectionsActions();
	const { fireUIAnalyticForFixButton } = useContext(AnalyticContext);
	const collectionsCount = useCollectionsDataCount();
	const hasReachedCollectionsLimitMessage = useHasReachedCollectionsLimitMessage();
	const reachedCollectionsLimitMessage = hasReachedCollectionsLimitMessage(collectionsCount);
	const collectionsIsLoading = useCollectionsIsLoading();
	const collectionsInitialized = useCollectionsInitialized();
	const collectionsError = useCollectionsError();
	const notifications = useNotifications();
	const onCreateRoadmapClick = useCallback(async () => {
		fireUIAnalyticForFixButton({ itemId: CREATE_ROADMAP });

		if (collectionsInitialized && !collectionsError) {
			return;
		}

		const init = async (refresh?: boolean) => {
			setIsLoading(true);
			try {
				const loadedCollections = await loadCollections({ refresh });
				const reachedLimitMessage = hasReachedCollectionsLimitMessage(loadedCollections.length);
				if (reachedLimitMessage) {
					notifications.info({
						title: formatMessage(infoTitleLimit),
						description: reachedLimitMessage,
					});
				}
			} catch {
				notifications.error({
					title: formatMessage(errorTitle),
					description: formatMessage(errorDescription),
					actionLabel: formatMessage(errorRefresh),
					actionTestId: 'create-roadmap',
					onActionClick: () => init(true),
				});
			} finally {
				setIsLoading(false);
			}
		};

		await init(Boolean(collectionsError));
	}, [
		collectionsError,
		collectionsInitialized,
		fireUIAnalyticForFixButton,
		formatMessage,
		hasReachedCollectionsLimitMessage,
		loadCollections,
		notifications,
	]);

	const { Panel, trigger } = usePanel();

	const onClosePanel = useCallback(() => {
		trigger.setPanel(null);
	}, [trigger]);

	const entryPointProps = useMemo(() => ({ onClose: onClosePanel }), [onClosePanel]);

	return (
		<>
			{!isReadOnly && (
				<>
					<Trigger
						trigger={trigger}
						entryPoint={createRoadmapPanelAsync}
						entryPointProps={entryPointProps}
					>
						{({ ref }) => (
							<Tooltip content={reachedCollectionsLimitMessage || formatMessage(createRoadmap)}>
								<CommonNavMenuButton
									// oxlint-disable-next-line typescript/consistent-type-assertions
									ref={ref as Ref<HTMLButtonElement>}
									icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
									label={formatMessage(createRoadmap)}
									isLoading={isLoading && collectionsIsLoading}
									isDisabled={Boolean(reachedCollectionsLimitMessage)}
									onClick={onCreateRoadmapClick}
								/>
							</Tooltip>
						)}
					</Trigger>
					<Panel />
				</>
			)}
		</>
	);
};
